'use client'
import { Flex, Text } from '@radix-ui/themes';
import React, { useEffect, useImperativeHandle, useState, useCallback, forwardRef, memo } from 'react';

import { Skeleton } from '@/app/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/app/components/ui/select';
import { isCreditCardExpired } from '@/lib/utils';
import { useParams } from 'next/navigation';
import { PaymentType, Role } from '@prisma/client';
import { TokenizedPM } from '@/lib/interface/stripe/tokenizedCard';
import CreditCardPopup from './CreditCardPopup';
import PaymentMethodSelectItem from './CreditCardSelectItem';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Button } from '../ui/button';
import { StorageService } from '@/app/services/storageService';

interface Props {
  initialize?: () => void;
  onValidCardSelected: () => void;
  onInvalidCardSelected: () => void;
  auctionId: number;
}

export interface SavePaymentSelectionHandle {
  savePaymentSelection: (paymentType?: PaymentType) => Promise<void>;
}

const PaymentMethodSelect = forwardRef<SavePaymentSelectionHandle, Props>((props, ref) => {
  const { initialize, onValidCardSelected, onInvalidCardSelected, auctionId } = props;
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<TokenizedPM[] | null>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [noCards, setNoCards] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(false);
  const { data: session }: any = useSession();
  const [profileComplete, setProfileComplete] = useState<boolean | undefined>(undefined);
  const router = useRouter();

  // Memoize the refresh function using useCallback
  const refreshCreditCardSelect = useCallback(() => {
    setRefresh(prev => !prev);
  }, []); // Empty dependency array: function instance is created only once

  useEffect(() => {
    const initProfileComplete = async () => {
      const complete = await fetch(`/api/v1/user/${session?.userId}/profile-complete`,
        { next: { tags: ['profile-complete'] } }).then(res => res.json());
      setProfileComplete(complete.profileComplete);
      if (!complete?.profileComplete) {
        // TODO: Add back in!
        // onInvalidCardSelected();
      }
    }
    initProfileComplete();
  }, [session?.userId, onInvalidCardSelected]);

  const savePaymentSelection = async (paymentType?: PaymentType) => {
    if (!paymentMethods) return;

    const selectedPaymentId = paymentMethods[selectedIndex].token;

    // Check if selection has changed in cache
    const cache = await StorageService.getPaymentCache(auctionId, session?.userId);

    if (cache?.selectedPaymentId === selectedPaymentId && cache?.persisted) {
      return; // No need to make API call if selection hasn't changed
    }

    await fetch(`/api/v1/paymentSelection`, {
      method: "POST",
      body: JSON.stringify({
        auctionId,
        paymentType,
        token: selectedPaymentId
      })
    });

    // Update cache after successful API call
    await StorageService.setPaymentCache(
      auctionId,
      paymentMethods,
      selectedPaymentId,
      session?.userId,
      true
    );
  };

  useImperativeHandle(ref, () => ({
    savePaymentSelection
  }))

  useEffect(() => {
    const getCreditCards = async () => {
      setLoading(true);

      // Try to get from cache first
      const cache = await StorageService.getPaymentCache(auctionId, session?.userId);

      if (cache) {
        setPaymentMethods(cache.paymentMethods);
        const idx = cache.paymentMethods.findIndex((c: TokenizedPM) =>
          c.token === cache.selectedPaymentId
        );
        setSelectedIndex(idx > -1 ? idx : 0);
        onValidCardSelected();
        setLoading(false);
        return;
      }

      // If not in cache, fetch from API
      const res = await fetch(`/api/v1/cc?auctionId=${auctionId}`).then(res => res.json());

      if (res?.error) {
        let errorMessage = "Failed to get user credit cards. Please try again later. We apologize for the inconvenience."

        if (res.error === "No stripe user.") {
          // errorMessage = "No payment cards were associated with your account. To bid, please add one in account settings."
          setNoCards(true);
          setLoading(false);
          return;
        }

        setError(errorMessage);
        onInvalidCardSelected();

        setLoading(false);
        return;
      } else {
        setNoCards(false);
      }

      if (Array.isArray(res) && res?.length > 0) {
        const idx = res.findIndex((c: TokenizedPM) => c.selected);
        setSelectedIndex(idx > -1 ? idx : 0);
        setPaymentMethods(res as TokenizedPM[]);

        // Cache the results
        await StorageService.setPaymentCache(
          auctionId,
          res,
          res[idx > -1 ? idx : 0].token,
          session?.userId
        );

        if (res.length > 0) {
          if (isCreditCardExpired(res[idx > -1 ? idx : 0])) {
            onInvalidCardSelected();
          } else {
            onValidCardSelected();
          }
        }
      }
      else {
        setPaymentMethods(null);
        setNoCards(true);
      }

      setLoading(false);
    }

    if (initialize) initialize();
    getCreditCards();
  }, [refresh, auctionId, initialize, onValidCardSelected, onInvalidCardSelected, session?.userId]);

  // Four cases: loading, no cards, error, full.
  if (loading || profileComplete === undefined) {
    return <Skeleton className="w-full h-14 border" />
  } else if (error) {
    return (
      <Flex className="w-full h-14 border p-2 rounded-md" justify="center" align="center" >
        <Text className="text-red-500 text-sm">{error}</Text>
      </Flex>
    )
  } else if (session?.role !== Role.BUYER) {
    return (
      <Flex className="w-full h-14 border p-3 pr-1.5 rounded-md" justify="between" align="center" >
        <Text className="text-sm">Please login to bid.</Text>
        <Button variant="outline" onClick={() => router.push(`/auth/signin?callbackUrl=${window.location.href}`)}>Login</Button>
      </Flex>
    )
  } else if (!profileComplete) {
    // "To proceed", rather than "To bid", since this theoretically could appear on the portal page.
    return (
      <Flex className="w-full h-14 border p-3 pr-1.5 rounded-md" justify="between" align="center" >
        <Text className="text-sm">Please complete your profile to proceed.</Text>
        <Button variant="outline" onClick={() => router.push(`/account/details`)}>Complete Profile</Button>
      </Flex>
    )
  } else if (noCards) {
    return (
      <Flex className="w-full h-14 border p-3 pr-1.5 rounded-md" justify="between" align="center" >
        <Text className="text-sm">No payment information on record.</Text>
        <CreditCardPopup refresh={refreshCreditCardSelect} />
      </Flex>
    )
  } else if (Array.isArray(paymentMethods) && paymentMethods.length > 0) {
    return (
      <Select onValueChange={
        (val: string) => {
          const idx = parseInt(val);
          setSelectedIndex(idx)
          // Don't allow confirmation on expired card
          if (isCreditCardExpired(paymentMethods[idx])) {
            onInvalidCardSelected();
          } else {
            onValidCardSelected();
          }
        }
      } >
        <SelectTrigger className="h-fit">
          <PaymentMethodSelectItem paymentMethod={paymentMethods[selectedIndex]} />
        </SelectTrigger>
        <SelectContent>
          {paymentMethods.map((pm: TokenizedPM, idx) =>
            <SelectItem key={idx} value={idx.toString()}>
              <PaymentMethodSelectItem paymentMethod={pm} />
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    );
  } else {
    return (
      <Flex className="w-full h-14 border p-2 rounded-md" justify="center" align="center" >
        Something went wrong.
      </Flex>
    )
  }
});

PaymentMethodSelect.displayName = "PaymentMethodSelect"

export default memo(PaymentMethodSelect)  
